import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight, Sparkles, Mail } from "lucide-react";
import { SOLUTIONS, BOOK_HREF } from "@/data/solutions";
import { useBooking } from "@/context/BookingContext";

// Dark, premium button for the public marketing site
export function MButton({ variant = "primary", size = "md", to, href, children, className = "", ...props }) {
  const booking = useBooking();
  const base = "inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-200 whitespace-nowrap";
  const sizes = { md: "h-10 px-5 text-sm", lg: "h-12 px-7 text-[15px]" };
  const variants = {
    primary: "bg-foreground text-background hover:opacity-90",
    secondary: "border border-border text-foreground hover:bg-secondary",
    ghost: "text-muted-foreground hover:text-foreground",
  };
  const cls = `${base} ${sizes[size]} ${variants[variant]} ${className}`;
  if (href && href === BOOK_HREF && booking) {
    return <button className={cls} onClick={booking.openBooking} {...props}>{children}</button>;
  }
  if (to) return <Link to={to} className={cls} {...props}>{children}</Link>;
  if (href) return <a href={href} className={cls} {...props}>{children}</a>;
  return <button className={cls} {...props}>{children}</button>;
}

export function Logo() {
  return (
    <Link to="/" className="inline-flex items-center group" data-testid="marketing-logo" aria-label="Aicom">
      <span className="logo-3d transition-transform duration-200 group-hover:-translate-y-0.5">
        <span className="lg-ai">AI</span><span className="lg-com">COM</span>
      </span>
    </Link>
  );
}

export function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobile(false); setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-[hsl(var(--background))]/75 backdrop-blur-md border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-1">
          <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <button data-testid="nav-solutions" className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              Løsninger <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[420px]"
                  data-testid="solutions-dropdown"
                >
                  <div className="rounded-2xl border border-border bg-card shadow-xl shadow-black/5 p-2">
                    {SOLUTIONS.map((s) => (
                      <Link key={s.slug} to={`/losninger/${s.slug}`} data-testid={`dropdown-${s.slug}`}
                        className="flex items-start gap-3 rounded-xl p-3 hover:bg-secondary transition-colors group">
                        <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0 group-hover:bg-foreground transition-colors">
                          <s.Icon className="h-5 w-5 text-muted-foreground group-hover:text-background transition-colors" strokeWidth={1.75} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{s.navTitle}</p>
                          <p className="text-xs text-muted-foreground leading-snug mt-0.5">{s.navDesc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/om" className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-om">Om</Link>
          <Link to="/inspiration" className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-inspiration">Inspiration</Link>
          <Link to="/login" className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-login">Log ind</Link>
          <MButton href={BOOK_HREF} className="ml-2" data-testid="nav-book">Book møde</MButton>
        </nav>

        <button className="md:hidden text-foreground" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-btn">
          {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-border bg-background overflow-hidden">
            <div className="px-6 py-4 space-y-1">
              <p className="text-xs uppercase tracking-widest text-muted-foreground px-1 py-2">Løsninger</p>
              {SOLUTIONS.map((s) => (
                <Link key={s.slug} to={`/losninger/${s.slug}`} className="flex items-center gap-3 py-2.5 text-sm text-foreground">
                  <s.Icon className="h-4 w-4" strokeWidth={1.75} /> {s.navTitle}
                </Link>
              ))}
              <Link to="/om" className="block py-2.5 text-sm text-foreground">Om</Link>
              <Link to="/inspiration" className="block py-2.5 text-sm text-foreground">Inspiration</Link>
              <Link to="/login" className="block py-2.5 text-sm text-foreground">Log ind</Link>
              <MButton href={BOOK_HREF} className="w-full mt-3">Book møde</MButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function MarketingFooter() {
  const booking = useBooking();
  const linkCls = "text-sm text-muted-foreground hover:text-foreground transition-colors duration-200";
  const labelCls = "text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-3";
  return (
    <footer className="border-t border-border bg-card">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-10">
        <div className="col-span-2">
          <Logo />
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-xs">Aicom giver virksomheder en AI-drevet kommunikationskapacitet, der før var forbeholdt de største.</p>
          <a href="mailto:hello@aicom.dk" className="inline-flex items-center gap-2 text-sm text-foreground hover:text-muted-foreground transition-colors mt-4" data-testid="footer-email-link">
            <Mail className="h-4 w-4" /> hello@aicom.dk
          </a>
        </div>
        <div>
          <p className={labelCls}>Løsninger</p>
          <ul className="space-y-2.5">
            {SOLUTIONS.map((s) => (
              <li key={s.slug}><Link to={`/losninger/${s.slug}`} className={linkCls}>{s.navTitle}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className={labelCls}>Læs mere</p>
          <ul className="space-y-2.5">
            <li><Link to="/om" className={linkCls}>Om</Link></li>
            <li><Link to="/inspiration" className={linkCls} data-testid="footer-inspiration-link">Inspiration</Link></li>
            <li><Link to="/beregner" className={linkCls} data-testid="footer-calculator-link">Annonceberegner</Link></li>
            <li><button onClick={booking?.openBooking} className={linkCls} data-testid="footer-book">Book møde</button></li>
            <li><button onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))} className={linkCls} data-testid="footer-cookie-settings">Cookieindstillinger</button></li>
            <li><Link to="/privatlivspolitik" className={linkCls} data-testid="footer-privacy-link">Cookie- og privatlivspolitik</Link></li>
          </ul>
        </div>
        <div>
          <p className={labelCls}>Aicom AI</p>
          <ul className="space-y-2.5">
            <li><Link to="/login" className={linkCls}>Log ind</Link></li>
            <li><Link to="/losninger/aicom-ai#priser" className={linkCls} data-testid="footer-tryfree-link">Prøv gratis</Link></li>
            <li><Link to="/handelsbetingelser" className={linkCls} data-testid="footer-terms-link">Handelsbetingelser</Link></li>
            <li><Link to="/sikkerhed-og-databehandling" className={linkCls} data-testid="footer-security-link">Sikkerhed & databehandling</Link></li>
            <li><Link to="/databehandleraftale" className={linkCls} data-testid="footer-dpa-link">Databehandleraftale (DPA)</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-5 text-xs text-muted-foreground/70">
          © {new Date().getFullYear()} Aicom — Din AI-drevne kommunikationsafdeling
        </div>
      </div>
    </footer>
  );
}

// Reusable bottom CTA used across marketing pages
export function BookCTA() {
  return (
    <section className="border-t border-border">
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.5 }} className="font-display text-3xl sm:text-5xl font-medium tracking-tight text-balance">
          Lad os tage en uforpligtende snak
        </motion.h2>
        <p className="text-muted-foreground mt-5 text-lg max-w-xl mx-auto leading-relaxed">
          Find ud af, om jeres virksomhed er klar til at træde ind i AI-æraen.
        </p>
        <div className="mt-9 flex items-center justify-center">
          <MButton href={BOOK_HREF} size="lg" data-testid="cta-book">Book møde <ArrowRight className="h-4 w-4" /></MButton>
        </div>
      </div>
    </section>
  );
}
